import { type yup } from '@framework/validation/initYup.ts';
import { useCallback, useMemo } from 'react';
import {
  type FieldValues,
  type Path,
  // biome-ignore lint/style/noRestrictedImports: wrapper interno
  useController as useRHFController,
} from 'react-hook-form';
import { useFormItemContext } from '../item/FormItemContext.tsx';
import { extractValueFromChangeArg } from './useController.ts';
import { useTriggerComputedSync } from './useTriggerComputedSync.ts';

/**
 * Utility centralizzata per gestire il controller (RHF)
 * di tutti i componenti di input.
 *
 * L'onChange è arricchito per sincronizzare i campi calcolati:
 * dopo ogni modifica utente, esegue `schema.cast()` in una microtask
 * per ricalcolare tutti i valori computed in un singolo passaggio
 * e aggiornare solo quelli che sono cambiati.
 */
export function useInputController<
  T = any,
  TFieldValues extends FieldValues = Record<string, T>,
>(
  name: Path<TFieldValues>,
  options: {
    enabled: boolean | undefined;
    schema?: yup.MixedSchema;
    /**
     * Permette di selezionare un formItemSchema diverso da quello di default (il primo).
     * Se non specificato, viene utilizzato il primo schema del formItem.
     *
     * In un FormItem con più nomi, gli schemi sono inseriti in ordine di definizione.
     * Ad esempio, se il name è ['field1', 'field2'], lo schema ritornato da useFormItemContext
     * conterrà gli schemi in ordine:
     * - formItemSchemas[0] -> schema per 'field1'
     * - formItemSchemas[1] -> schema per 'field2'
     */
    customSchemaIndex?: number;
    /**
     * Se `true`, non esegue il sync dei computed values nell'onChange.
     * Utilizzato per i campi computed stessi, che sono di sola lettura
     * e non devono ri-triggerare il cast.
     */
    skipComputedSync?: boolean;
  },
) {
  const { customSchemaIndex } = options;
  // `enabled` è mantenuto nell'interfaccia per compatibilità con i chiamanti,
  // ma non è più utilizzato internamente: il reset su disabilitazione è gestito
  // dal transform dello schema Yup tramite syncComputedValues.
  const { getSchema } = useFormItemContext();

  const inputSchema = useMemo(
    () => options.schema ?? getSchema?.(customSchemaIndex ?? 0) ?? undefined,
    [customSchemaIndex, getSchema, options.schema],
  );

  const controller = useRHFController<TFieldValues>({
    name,
    defaultValue: inputSchema?.getDefault(),
  });

  const triggerSync = useTriggerComputedSync();
  const skipSync = options.skipComputedSync;

  const enhancedOnChange: typeof controller.field.onChange = useCallback(
    (...args) => {
      const value = extractValueFromChangeArg(args[0]);
      controller.field.onChange(...args);
      if (!skipSync) triggerSync({ path: name, value });
    },
    [controller.field.onChange, name, skipSync, triggerSync],
  );

  return {
    ...controller,
    field: {
      ...controller.field,
      onChange: enhancedOnChange,
    },
  };
}
